import { Box, Stack, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { EntityBase } from '@provision/entity-types';
import { portalPaths, UrlParams } from '@provision/portal-routes';

import {
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isNullish, pathOr, stringToPath } from 'remeda';
import { DynamicIcon, EllipsesMenu, StandaloneBadge } from 'src/components';
import {
  type QuickAction,
  useQuickActionsContext,
} from 'src/components/QuickActions/quick-actions-context';
import { appShortcuts } from 'src/constants/appShortcuts';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  useGroupRules,
  UserGroupRules,
  useViewRecordFolders,
  VIEW_ASSIGNMENT_ENTITIES,
} from 'src/graphql';

import {
  CustomViewEntity,
  mapConditionsForApi,
  useCustomViewEntityConfig,
  useCustomViews,
} from 'src/graphql/entities/custom-view';
import {
  type BulkUpdateEntitiesProgress,
  isProgressTerminal,
  useBulkUpdateEntitiesProgressQuery,
  useUndoBulkUpdateEntitiesMutation,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  type BulkCopyContext,
  type BulkCopyEntity,
  formatBulkCopyLinks,
  formatBulkCopyValues,
  getBulkCopyActions,
} from 'src/graphql/entities/custom-view/custom-view-config/bulk-copy-config';
import {
  type BulkActionContextValues,
  getBulkUpdateActions,
  mapCustomViewEntityToEntityName,
} from 'src/graphql/entities/custom-view/custom-view-config/bulk-update-config';
import {
  CustomAttributesRow,
  getCustomAttributeDisplayPaths,
  isCustomAttributeColumnId,
} from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';

import {
  useCreateRecordFolderAssignment,
  useDeleteRecordFolderAssignment,
} from 'src/graphql/entities/record-folder-assignment';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useBulkCopyConversationAsMarkdown } from 'src/pages/Conversations/use-bulk-copy-conversation-as-markdown';
import { BulkActionBar } from 'src/pages/CustomViews/components/bulk-action-bar';
import {
  BulkActionsModal,
  type BulkActionsModalAction,
} from 'src/pages/CustomViews/components/bulk-actions-modal';
import { BulkOverlay } from 'src/pages/CustomViews/components/bulk-overlay';
import { BulkUpdateJobBanner } from 'src/pages/CustomViews/components/bulk-update-job-banner';
import { FavoriteCheckbox } from 'src/pages/CustomViews/custom-views-list-view';
import { useBulkSelectionState } from 'src/pages/CustomViews/hooks/use-bulk-selection-state';
import { useCopyCustomView } from 'src/pages/CustomViews/hooks/use-copy-custom-view';
import { useCustomViewState } from 'src/pages/CustomViews/hooks/use-custom-view-state';
import { RecordsGridLayout } from 'src/pages/CustomViews/records-grid-layout';
import { shouldShowBulkUpdateAction } from 'src/pages/CustomViews/utils';
import {
  getBulkUpdateViewContextKey,
  isBulkUpdateJobInPartnerScope,
  useBulkUpdateJobsStore,
  useGlobalStore,
} from 'src/store';
import { Modals, useCreateModalByEntity, useModalMethods } from 'src/store/modals';
import { triggerCSVExport } from 'src/utils/csv';
import {
  useConfirmationModal,
  useCopyToClipboard,
  useEntityFullUrl,
  useNotifications,
  useRegisterShortcuts,
  useScrollPaginateHandler,
} from 'src/utils/hooks';

import { AppEvents, emitAppEvent, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';
import { formatCount } from 'src/utils/number';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { CreateButtonProps } from './components/create-button';
import { CONVERSATIONS_VIEW_WRAPPER_STYLES } from './constants';
import { useInitDefaultBaseSelectedView } from './hooks/use-init-default-base-selected-view';
import { useSelectedViewUrlContext } from './hooks/use-selected-view-url-context';

const DEFAULT_RECORDS_LIMIT = 30;

const DEFAULT_EXPORT_RECORDS_LIMIT = 100;
const ACCESS_TO_BULK_EDIT_ATTRIBUTES_RULE = 'access_to_bulk_edit_attributes' as UserGroupRules;

export const GridBreadcrumbItem = ({
  handleClick,
  text = 'Views',
}: {
  handleClick: () => void;
  text?: string;
}) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Box sx={{ cursor: 'pointer', ':hover': { opacity: 0.8 } }} onClick={handleClick}>
        <Typography variant="subtitle2">{text}</Typography>
      </Box>
      <DynamicIcon name="chevron_right" fontSize="1.4rem" />
    </Box>
  );
};

export const BaseSelectedView = <T extends EntityBase>({
  entity,
  viewId,
  title,
  slots,
  slotProps,
  onRecordClose,
  onRecordClick,
  onRecordOpenFullPage,
  selectedRecord,
  sx,
  leftPanelWidth,
  leftPanelMinWidth,
  viewFilters,
  leftHeaderAdornment,
  isStaticView = false,
  hasDetachedPanel,
  bulkActionContextValues,
}: {
  viewId?: string;
  title?: string;
  entity: CustomViewEntity;
  slots?: {
    rightPanel?: ReactNode;
    createButton?: ReactElement;
  };
  slotProps?: {
    createButton?: CreateButtonProps;
  };
  selectedRecord?: T | null;
  sx?: SxProps<Theme>;
  leftPanelWidth?: number;
  leftPanelMinWidth?: number;
  onRecordClose?: () => void;
  onRecordClick?: (id: T['id'], record: T, event?: MouseEvent) => void;
  /** Enter on the selected row calls this instead of onRecordClick — the host's "open full page" navigation. */
  onRecordOpenFullPage?: (id: T['id'], record: T) => void;
  viewFilters?: ConditionInput[];
  leftHeaderAdornment?: ReactNode;
  isStaticView?: boolean;
  hasDetachedPanel?: boolean;
  /**
   * Values this page supplies for bulk actions that take their value from context rather than an
   * editor. Actions declaring `ValueSource.HostContext` are offered only when their value is here.
   */
  bulkActionContextValues?: BulkActionContextValues;
}) => {
  const { navigate, partnerPrefix } = useNavigateRoute();
  const { groupRules } = useGroupRules();
  const { confirm } = useConfirmationModal();
  const { customViewId } = useRouteParams(UrlParams.customViewId);
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const theme = useTheme();
  const [isCsvExportInProgress, setIsCsvExportInProgress] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isBulkActionsModalOpen, setIsBulkActionsModalOpen] = useState(false);
  const [initialBulkUpdateAttribute, setInitialBulkUpdateAttribute] = useState<string | null>(
    null,
  );

  const selectedViewUrlContext = useSelectedViewUrlContext(entity);

  const { setModalProps: setAddSiteModalProps } = useModalMethods(Modals.AddSiteModal);
  const { setModalProps: setAddClientModalProps } = useModalMethods(Modals.AddClientModal);
  const { setModalProps: setAddProjectModalProps } = useModalMethods(Modals.AddProject);

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const activeBulkUpdateJob = useBulkUpdateJobsStore.use.activeBulkUpdateJob();
  const pendingBulkUpdateRefreshContextKeys =
    useBulkUpdateJobsStore.use.pendingBulkUpdateRefreshContextKeys();
  const startBulkUpdateJob = useBulkUpdateJobsStore.use.startBulkUpdateJob();
  const updateBulkUpdateJob = useBulkUpdateJobsStore.use.updateBulkUpdateJob();
  const dismissBulkUpdateJob = useBulkUpdateJobsStore.use.dismissBulkUpdateJob();
  const clearBulkUpdateViewRefreshFlag =
    useBulkUpdateJobsStore.use.clearBulkUpdateViewRefreshFlag();
  const markBulkUpdateViewForRefresh =
    useBulkUpdateJobsStore.use.markBulkUpdateViewForRefresh();
  const setBulkUpdateSourceViewContextKey =
    useBulkUpdateJobsStore.use.setBulkUpdateSourceViewContextKey();
  const selectedCustomViewId = viewId || customViewId;
  const resolvedViewId = selectedCustomViewId ?? `${entity}-static`;
  const currentViewContextKey = useMemo(
    () => getBulkUpdateViewContextKey(entity as EntityNames, resolvedViewId),
    [entity, resolvedViewId],
  );
  const isActiveBulkJobForCurrentView = Boolean(
    activeBulkUpdateJob &&
      activeBulkUpdateJob.entityName === entity &&
      activeBulkUpdateJob.viewId === resolvedViewId,
  );
  const isActiveBulkJobInCurrentScope = isBulkUpdateJobInPartnerScope(
    activeBulkUpdateJob,
    selectedPartnerId,
  );
  const activeBulkUpdateJobId =
    isActiveBulkJobForCurrentView && isActiveBulkJobInCurrentScope
      ? activeBulkUpdateJob?.jobId
      : null;

  useInitDefaultBaseSelectedView({
    entityName: entity,
    viewId: selectedCustomViewId,
    skip: !isStaticView && !selectedViewUrlContext.isStaticView,
  });

  const goToViewList = useCallback(
    () => navigate(portalPaths.customView.view(entity)),
    [navigate, entity],
  );

  const { customViews, toggleFavorite, deleteCustomView, updateCustomView } = useCustomViews({
    entity,
    partnerId: selectedPartnerId,
  });

  const customView = useMemo(
    () => customViews.find((i) => i.customViewId === selectedCustomViewId),
    [customViews, selectedCustomViewId],
  );

  const {
    isQueryBuilderOpen,
    filters,
    columns,
    formMethods,
    sort,
    mappedConditions,
    queryParams,
    handleFiltersUpdate: handleFilterChange,
    handleSortChange,
    handleColumnUpdate,
    handleColumnsUpdate,
    toggleQueryBuilder,
    resetToDefault,
  } = useCustomViewState({ id: selectedCustomViewId, customView, entity });

  const { attributesList, useViewEntities, isSearchable, columnRenderersById } =
    useCustomViewEntityConfig(entity);

  const entityFilters = useMemo(() => {
    return [...(viewFilters || []), ...mappedConditions];
  }, [mappedConditions, viewFilters]);

  // display `::text` paths for the selected custom-attribute columns; [] (dormant) for built-in-only views.
  const dynamicAttributePaths = useMemo(
    () => getCustomAttributeDisplayPaths(columns),
    [columns],
  );

  const {
    records = [],
    isLoading: recordsLoading,
    isLoadingMore: recordsLoadingMore,
    totalRecords,
    hasMore,
    loadMore,
    cacheManager,
  } = useViewEntities({
    filters: entityFilters,
    searchTerm,
    sort: sort || customView?.sort,
    limit: DEFAULT_RECORDS_LIMIT,
    dynamicAttributePaths,
    ...queryParams,
  }) ?? {};

  const {
    data: bulkUpdateProgressData,
    loading: isBulkUpdateProgressLoading,
    refetch: refetchBulkUpdateProgress,
  } = useBulkUpdateEntitiesProgressQuery(activeBulkUpdateJobId, 2_000);
  const [undoBulkUpdate, { loading: isUndoInProgress }] = useUndoBulkUpdateEntitiesMutation();
  const bulkUpdateProgress = (
    bulkUpdateProgressData as
      | {
          bulkUpdateEntitiesProgress?: BulkUpdateEntitiesProgress;
        }
      | undefined
  )?.bulkUpdateEntitiesProgress;

  const {
    selection,
    clearSelection,
    isRecordSelected,
    setRecordSelected,
    setAllVisibleSelected,
    isAllVisibleSelected,
    isSomeVisibleSelected,
    totalSelectedCount,
    selectedVisibleRecords = [],
  } = useBulkSelectionState({
    records: records as unknown as T[],
    totalRecords,
  });

  // clear selection when view context changes
  useEffect(() => {
    clearSelection();
  }, [
    clearSelection,
    entity,
    selectedCustomViewId,
    searchTerm,
    sort?.attribute,
    sort?.desc,
    mappedConditions,
    viewFilters,
  ]);

  useEffect(
    () =>
      subscribeToAppEvent(AppEvents.SearchRecordsInvalidated, ({ entityName, viewId }) => {
        if ((entityName && entityName !== entity) || (viewId && viewId !== resolvedViewId)) {
          return;
        }

        cacheManager.reset({ refetch: true });
      }),
    [cacheManager, entity, resolvedViewId],
  );

  useEffect(() => {
    if (
      !bulkUpdateProgress ||
      !isProgressTerminal(bulkUpdateProgress.status) ||
      !activeBulkUpdateJobId
    ) {
      return;
    }

    if (!activeBulkUpdateJob?.hasTerminalSyncApplied) {
      emitAppEvent(AppEvents.SearchRecordsInvalidated, {
        entityName: entity as EntityNames,
        viewId: resolvedViewId,
      });
      markBulkUpdateViewForRefresh(currentViewContextKey);
      updateBulkUpdateJob(activeBulkUpdateJobId, {
        hasTerminalSyncApplied: true,
        isUndoInProgress: false,
      });
    }

    cacheManager.reset({ refetch: true });
  }, [
    activeBulkUpdateJob?.hasTerminalSyncApplied,
    activeBulkUpdateJobId,
    bulkUpdateProgress,
    cacheManager,
    currentViewContextKey,
    entity,
    markBulkUpdateViewForRefresh,
    resolvedViewId,
    updateBulkUpdateJob,
  ]);

  useEffect(() => {
    if (!pendingBulkUpdateRefreshContextKeys.includes(currentViewContextKey)) {
      return;
    }

    cacheManager.reset({ refetch: true });
    clearBulkUpdateViewRefreshFlag(currentViewContextKey);
  }, [
    cacheManager,
    clearBulkUpdateViewRefreshFlag,
    currentViewContextKey,
    pendingBulkUpdateRefreshContextKeys,
  ]);

  useEffect(() => {
    if (!isActiveBulkJobForCurrentView || activeBulkUpdateJob?.dismissed) {
      setBulkUpdateSourceViewContextKey(null);
      return;
    }

    setBulkUpdateSourceViewContextKey(currentViewContextKey);

    return () => {
      setBulkUpdateSourceViewContextKey(null);
    };
  }, [
    activeBulkUpdateJob?.dismissed,
    currentViewContextKey,
    isActiveBulkJobForCurrentView,
    setBulkUpdateSourceViewContextKey,
  ]);

  const canExportData = groupRules[UserGroupRules.ExportData] && !!records?.length;
  const canCopyConversationAsMarkdown = Boolean(
    groupRules[UserGroupRules.AccessToCopyConversationAsMarkdown],
  );
  const canBulkEdit = groupRules[ACCESS_TO_BULK_EDIT_ATTRIBUTES_RULE] === true;

  const canBulkSelect = true;

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 800,
    limit: DEFAULT_RECORDS_LIMIT,
  });

  const handleFavoriteClick = useCallback(
    (id: string, isFavorite: boolean) => toggleFavorite(id, { isFavorite }),
    [toggleFavorite],
  );

  // using refs for optimization purposes
  const recordsRef = useRef(records);
  const hasMoreRef = useRef(hasMore);

  useEffect(() => {
    recordsRef.current = records;

    return () => {
      recordsRef.current = [];
    };
  }, [records]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    if (!canBulkSelect) {
      clearSelection();
      setIsBulkActionsModalOpen(false);
      setInitialBulkUpdateAttribute(null);
    }
  }, [canBulkSelect, clearSelection]);

  const handleExportClick = useCallback(async () => {
    try {
      setIsCsvExportInProgress(true);

      // downloading pages one by one until everything is ready to be exported
      while (hasMoreRef.current) {
        if (!recordsRef.current?.length) {
          return;
        }

        await loadMore(DEFAULT_EXPORT_RECORDS_LIMIT, recordsRef.current.length);

        // small delay to let the state to be propagated
        await new Promise((res) => setTimeout(res, 500));
      }

      const headers = columns?.map((i) => i.attributeLabel ?? '') ?? [];

      // preparing data based on selected columns for each conversation
      const dataRows = recordsRef.current.map((c) =>
        columns?.map((column) =>
          // custom attribute values are decoded from the row's customAttributes blob, not a nested path
          isCustomAttributeColumnId(column.attribute)
            ? (columnRenderersById
                ?.get(column.attribute)
                ?.getStringValue(c as CustomAttributesRow) ?? '')
            : (pathOr(c, stringToPath(column.accessor), '') ?? ''),
        ),
      ) as string[][];

      triggerCSVExport(
        `${entity}_records_${customView?.name?.toLowerCase() ?? ''}`,
        [headers].concat(dataRows),
      );

      showSuccessAlert('Export has been successfully completed');
    } catch (error) {
      console.error(error);
      showErrorAlert('Something went wrong during export');
    } finally {
      setIsCsvExportInProgress(false);
    }
  }, [
    columns,
    entity,
    loadMore,
    showSuccessAlert,
    showErrorAlert,
    customView,
    columnRenderersById,
  ]);

  const isAllowedToUpdate = !!customView && selectedPartnerId === customView?.partnerId;

  const { manageableRecordFolders: recordFolders } = useViewRecordFolders();

  const { createRecordFolderAssignment } = useCreateRecordFolderAssignment({
    entityFilter: VIEW_ASSIGNMENT_ENTITIES,
  });
  const { deleteRecordFolderAssignment } = useDeleteRecordFolderAssignment();

  const menuOptions = useMemo(() => {
    const list = [];

    if (isAllowedToUpdate) {
      list.push({
        label: 'Update',
        onClick: () =>
          navigate(
            portalPaths.customView.action(customView?.entityName, customView?.customViewId),
          ),
      });

      list.push({
        label: 'Delete',
        onClick: async () => {
          if (
            await confirm({
              title: 'Delete view?',
              body: `${customView.name ?? 'This view'} will be deleted permanently.`,
              confirmLabel: 'Delete view',
              danger: true,
            })
          ) {
            void deleteCustomView(customView.customViewId);
          }
        },
      });
    }

    if (canExportData) {
      list.push({
        label: 'Export as CSV',
        onClick: handleExportClick,
      });
    }

    if (recordFolders?.length && customView) {
      list.push({
        label: 'Add to folder...',
        subItems: recordFolders.map((i) => {
          const assignment = i.assignments?.find((i) => i.entityId === customView.id);

          return {
            id: i.id,
            label: i.name,
            icon: 'folder',
            iconColor: assignment ? theme.palette.custom.OV.sys.secondary : undefined,
            selected: Boolean(assignment),
            onClick: () => {
              if (assignment) {
                void deleteRecordFolderAssignment(assignment);
              } else {
                void createRecordFolderAssignment({
                  recordFolderId: i.id,
                  entity: 'CustomView',
                  entityId: customView.id,
                  partnerId: selectedPartnerId ?? PLATFORM_PARTNER_ID,
                  sortOrder: 0,
                  title: customView.name,
                });
              }
            },
          };
        }),
      });
    }

    return list;
  }, [
    isAllowedToUpdate,
    canExportData,
    recordFolders,
    customView,
    navigate,
    confirm,
    deleteCustomView,
    handleExportClick,
    createRecordFolderAssignment,
    deleteRecordFolderAssignment,
    selectedPartnerId,
    theme,
  ]);

  // --- Bulk selection helpers ---

  const entityUrlBase = useEntityFullUrl(
    selectedPartnerId ?? 0,
    entity,
    '__ENTITY_ID_PLACEHOLDER__',
  );

  const getEntityUrl = useCallback(
    (entityId: string): string => {
      if (!entityUrlBase) return '';
      return entityUrlBase.replace('__ENTITY_ID_PLACEHOLDER__', encodeURIComponent(entityId));
    },
    [entityUrlBase],
  );

  const displayTotalCount = Math.max(totalRecords || 0, records.length, totalSelectedCount);

  const openBulkActionsModal = useCallback(
    (attributeKey?: string) => {
      if (canBulkSelect && totalSelectedCount > 0) {
        setInitialBulkUpdateAttribute(attributeKey ?? null);
        setIsBulkActionsModalOpen(true);
      }
    },
    [canBulkSelect, totalSelectedCount],
  );

  const closeBulkActionsModal = useCallback(() => {
    setIsBulkActionsModalOpen(false);
    setInitialBulkUpdateAttribute(null);
  }, []);

  const activeBulkUpdateOperationKind =
    isActiveBulkJobForCurrentView && activeBulkUpdateJob?.operationKind
      ? activeBulkUpdateJob.operationKind
      : 'update';

  const handleBulkUpdateSubmitted = useCallback(
    (jobId: string, operationKind: 'update' | 'delete' = 'update') => {
      if (operationKind === 'delete') {
        const cache = cacheManager?.client?.cache;
        const typename = mapCustomViewEntityToEntityName(entity);

        if (cache) {
          for (const id of selection.selectedIds) {
            cache.evict({ id: cache.identify({ __typename: typename, id }) });
          }
          cache.gc();
        }
      }

      startBulkUpdateJob({
        jobId,
        entityName: entity as EntityNames,
        viewId: resolvedViewId,
        partnerId: selectedPartnerId ?? PLATFORM_PARTNER_ID,
        operationKind,
      });
      clearSelection();
    },
    [
      cacheManager,
      clearSelection,
      entity,
      resolvedViewId,
      selectedPartnerId,
      selection.selectedIds,
      startBulkUpdateJob,
    ],
  );

  const closeBulkUpdateBanner = useCallback(() => {
    if (!activeBulkUpdateJobId) {
      return;
    }
    dismissBulkUpdateJob(activeBulkUpdateJobId);
  }, [activeBulkUpdateJobId, dismissBulkUpdateJob]);

  const handleUndoBulkUpdate = useCallback(async () => {
    if (!bulkUpdateProgress?.jobId || !isActiveBulkJobForCurrentView) {
      return;
    }

    try {
      updateBulkUpdateJob(bulkUpdateProgress.jobId, { isUndoInProgress: true });
      await undoBulkUpdate({
        variables: {
          input: {
            jobId: bulkUpdateProgress.jobId,
          },
        },
      });

      await refetchBulkUpdateProgress?.({ input: { jobId: bulkUpdateProgress.jobId } });
    } catch (error) {
      updateBulkUpdateJob(bulkUpdateProgress.jobId, { isUndoInProgress: false });
      showErrorAlert(error instanceof Error ? error.message : 'Failed to undo bulk update');
    }
  }, [
    bulkUpdateProgress?.jobId,
    isActiveBulkJobForCurrentView,
    refetchBulkUpdateProgress,
    showErrorAlert,
    undoBulkUpdate,
    updateBulkUpdateJob,
  ]);

  const isUndoBannerInProgress =
    isUndoInProgress ||
    (isActiveBulkJobForCurrentView && Boolean(activeBulkUpdateJob?.isUndoInProgress));
  const handleBulkActionCompleted = useCallback(() => {
    cacheManager.reset({ refetch: true });
    clearSelection();
  }, [cacheManager, clearSelection]);

  const isBulkUpdateBannerVisible = useMemo(() => {
    if (!activeBulkUpdateJobId || !isActiveBulkJobForCurrentView) {
      return false;
    }

    if (activeBulkUpdateJob?.dismissed) {
      return false;
    }

    return isBulkUpdateProgressLoading || Boolean(bulkUpdateProgress);
  }, [
    activeBulkUpdateJob?.dismissed,
    activeBulkUpdateJobId,
    isActiveBulkJobForCurrentView,
    isBulkUpdateProgressLoading,
    bulkUpdateProgress,
  ]);

  // Register Cmd+Shift+K shortcut
  useRegisterShortcuts(
    useMemo(
      () => [
        {
          ...appShortcuts.bulkActions,
          callback: () => openBulkActionsModal(),
        },
        {
          ...appShortcuts.selectAllVisibleTableRows,
          callback: (event: KeyboardEvent) => {
            event.preventDefault();
            setAllVisibleSelected(true);
            return false;
          },
        },
      ],
      [openBulkActionsModal, setAllVisibleSelected],
    ),
  );

  const selectionState = useMemo(() => {
    return {
      isRowSelected: (id: string) => isRecordSelected(id),
      onRowSelectionChange: setRecordSelected,
      onToggleSelectAllVisible: setAllVisibleSelected,
      isAllVisibleSelected,
      isSomeVisibleSelected,
    };
  }, [
    isRecordSelected,
    setRecordSelected,
    setAllVisibleSelected,
    isAllVisibleSelected,
    isSomeVisibleSelected,
  ]);

  const bulkUpdateSelectionInput = useMemo(() => {
    return {
      mode: 'manual' as const,
      manual: {
        selectedIds: Array.from(selection.selectedIds),
      },
    };
  }, [selection.selectedIds]);

  const customTitle = useMemo(
    () => (
      <Stack flexDirection="row" alignItems="center" flex="1 1 auto">
        <Typography variant="subtitle2" sx={{ marginRight: '0.3rem' }}>
          {title || customView?.name}
        </Typography>
        <Box mx="0.8rem">
          <StandaloneBadge badgeContent={formatCount(totalRecords)} />
        </Box>

        {!!menuOptions?.length && (
          <EllipsesMenu
            options={menuOptions}
            title="Actions"
            isLoading={isCsvExportInProgress}
          />
        )}

        {!!customView && (
          <FavoriteCheckbox
            checked={!!customView?.isFavorite}
            onClick={() => {
              handleFavoriteClick(customView.customViewId, !customView.isFavorite);
            }}
          />
        )}
      </Stack>
    ),
    [title, customView, totalRecords, menuOptions, isCsvExportInProgress, handleFavoriteClick],
  );

  const { showModal } = useCreateModalByEntity(entity);

  const { goToCopyView } = useCopyCustomView();

  const handleCopyClick = useCallback(() => {
    if (!customView) {
      return;
    }

    const conditions = filters || customView.formConditions;
    const sorting = sort || customView.sort;
    const selectedColumns = columns || customView.columns;

    return goToCopyView({
      ...customView,
      formConditions: conditions,
      sort: sorting,
      columns: selectedColumns,
    });
  }, [customView, filters, sort, columns, goToCopyView]);

  const handleUpdateOriginView = useCallback(() => {
    if (!isAllowedToUpdate || !customView) {
      return;
    }

    const sorting = sort || customView.sort;
    const selectedColumns = columns || customView.columns;

    updateCustomView(
      customView,
      {
        ...customView,
        conditions: mapConditionsForApi(filters!),
        sort: sorting,
        columns: selectedColumns,
      },
      () => {
        resetToDefault?.();
        showSuccessAlert('The view has been updated successfully');
      },
    );
  }, [
    resetToDefault,
    updateCustomView,
    showSuccessAlert,
    filters,
    sort,
    columns,
    customView,
    isAllowedToUpdate,
  ]);

  const createEntityProps = useMemo(
    () => ({
      sort: sort ? { attribute: sort.attribute, desc: sort.desc } : undefined,
      filters: mappedConditions,
      limit: DEFAULT_RECORDS_LIMIT,
    }),
    [mappedConditions, sort],
  );

  const onCreateClick = useCallback(() => {
    showModal(createEntityProps);
  }, [showModal, createEntityProps]);

  const { updateAndRestoreAction, addActions, removeActions } = useQuickActionsContext();

  useEffect(() => {
    let deleteModalProps: () => void;

    if (entity === EntityNames.Client) {
      deleteModalProps = setAddClientModalProps(createEntityProps);
    } else if (entity === EntityNames.Site) {
      deleteModalProps = setAddSiteModalProps(createEntityProps);
    } else if (entity === EntityNames.Project) {
      deleteModalProps = setAddProjectModalProps(createEntityProps);
    }

    return () => {
      deleteModalProps?.();
    };
  }, [
    onCreateClick,
    entity,
    updateAndRestoreAction,
    setAddClientModalProps,
    createEntityProps,
    setAddSiteModalProps,
    setAddProjectModalProps,
  ]);

  const { copyToClipboard } = useCopyToClipboard();
  const { copyConversationsAsMarkdown, isCopyConversationsAsMarkdownLoading } =
    useBulkCopyConversationAsMarkdown();
  const bulkCopyActionDefs = useMemo(
    () => getBulkCopyActions(entity as BulkCopyEntity),
    [entity],
  );
  const selectedConversationsForMarkdown = useMemo(() => {
    if (entity !== EntityNames.Conversation) {
      return [];
    }

    return selectedVisibleRecords
      .map((record) => {
        const conversationRecord = record as Record<string, unknown> & {
          id?: string | number;
        };

        return {
          conversationId: String(
            conversationRecord.conversationId ?? conversationRecord.id ?? '',
          ),
          friendlyId: String(conversationRecord.friendlyId ?? conversationRecord.id ?? ''),
          partnerId: Number(conversationRecord.partnerId ?? selectedPartnerId ?? 0),
          subject: String(conversationRecord.subject ?? ''),
          createdAt:
            typeof conversationRecord.createdAt === 'string'
              ? conversationRecord.createdAt
              : '',
          conversationStatusUpdatedAt:
            typeof conversationRecord.conversationStatusUpdatedAt === 'string'
              ? conversationRecord.conversationStatusUpdatedAt
              : '',
        };
      })
      .filter((conversation) => conversation.conversationId && conversation.friendlyId);
  }, [entity, selectedVisibleRecords, selectedPartnerId]);

  const handleBulkCopyConversationsAsMarkdown = useCallback(() => {
    if (!selectedConversationsForMarkdown.length) {
      return;
    }

    void copyConversationsAsMarkdown({
      conversations: selectedConversationsForMarkdown,
      getConversationUrl: (conversation) => getEntityUrl(conversation.friendlyId),
    });
  }, [copyConversationsAsMarkdown, getEntityUrl, selectedConversationsForMarkdown]);

  const bulkMarkdownModalAction = useMemo<BulkActionsModalAction | null>(() => {
    if (entity !== EntityNames.Conversation) {
      return null;
    }

    return {
      id: 'copyConversationsAsMarkdown',
      label: isCopyConversationsAsMarkdownLoading
        ? 'Copying Conversations as Markdown...'
        : 'Copy Conversations as Markdown',
      icon: 'file-text',
      isLoading: isCopyConversationsAsMarkdownLoading,
      isAllowed: canCopyConversationAsMarkdown,
      handler: handleBulkCopyConversationsAsMarkdown,
    };
  }, [
    canCopyConversationAsMarkdown,
    entity,
    handleBulkCopyConversationsAsMarkdown,
    isCopyConversationsAsMarkdownLoading,
  ]);
  const bulkUpdateActionDefs = useMemo(() => {
    return getBulkUpdateActions(entity, {
      isMultitenancy: isNullish(selectedPartnerId),
      contextValues: bulkActionContextValues,
    });
  }, [entity, selectedPartnerId, bulkActionContextValues]);

  const canBulkDelete = useMemo(
    () =>
      bulkUpdateActionDefs.some(
        (action) => action.kind === 'delete' && shouldShowBulkUpdateAction(action, groupRules),
      ),
    [bulkUpdateActionDefs, groupRules],
  );

  const bulkQuickActions = useMemo<QuickAction[]>(() => {
    if (!canBulkSelect || totalSelectedCount === 0) return [];

    const ctx: BulkCopyContext = { getEntityUrl };

    const copyActions = bulkCopyActionDefs.map((action) => ({
      id: `bulk-${action.id}`,
      title: action.label,
      description: `${totalSelectedCount} item${totalSelectedCount === 1 ? '' : 's'}`,
      icon: <DynamicIcon name={action.icon} library="lucide" />,
      group: 'Bulk Actions',
      order: -1,
      keywords: [action.label, 'Bulk Actions', 'copy', 'bulk'],
      handler: () => {
        const values = selectedVisibleRecords.map((r) => action.resolver(r, ctx));
        if (action.isFriendlyLink) {
          void copyToClipboard(formatBulkCopyLinks(values));
        } else {
          void copyToClipboard(formatBulkCopyValues(values));
        }
      },
    }));

    const markdownAction =
      entity === EntityNames.Conversation && canCopyConversationAsMarkdown
        ? [
            {
              id: 'bulk-copy-conversations-as-markdown',
              title: isCopyConversationsAsMarkdownLoading
                ? 'Copying Conversations as Markdown...'
                : 'Copy Conversations as Markdown',
              description: `${totalSelectedCount} item${totalSelectedCount === 1 ? '' : 's'}`,
              icon: <DynamicIcon name="file-text" library="lucide" />,
              group: 'Bulk Actions',
              order: -1,
              keywords: ['Copy Conversations as Markdown', 'Bulk Actions', 'copy', 'bulk'],
              handler: handleBulkCopyConversationsAsMarkdown,
            },
          ]
        : [];

    const updateActions = bulkUpdateActionDefs
      .filter((action) => {
        if (!shouldShowBulkUpdateAction(action, groupRules)) {
          return false;
        }

        return action.kind === 'delete' ? true : canBulkEdit;
      })
      .map((action) => ({
        id: `bulk-${action.id}`,
        title: action.label,
        description: `${totalSelectedCount} item${totalSelectedCount === 1 ? '' : 's'}`,
        icon: <DynamicIcon name={action.icon} library="lucide" />,
        group: 'Bulk Actions',
        order: -1,
        keywords: [action.label, action.attributeLabel, 'Bulk Actions', 'update', 'bulk'],
        handler: () => openBulkActionsModal(action.attributeKey),
      }));

    return [...copyActions, ...markdownAction, ...updateActions];
  }, [
    canCopyConversationAsMarkdown,
    canBulkSelect,
    canBulkEdit,
    totalSelectedCount,
    bulkCopyActionDefs,
    bulkUpdateActionDefs,
    groupRules,
    entity,
    getEntityUrl,
    handleBulkCopyConversationsAsMarkdown,
    isCopyConversationsAsMarkdownLoading,
    selectedVisibleRecords,
    copyToClipboard,
    openBulkActionsModal,
  ]);

  useEffect(() => {
    if (bulkQuickActions.length > 0) {
      addActions(bulkQuickActions);
    }
    return () => {
      if (bulkQuickActions.length > 0) {
        removeActions(bulkQuickActions);
      }
    };
  }, [bulkQuickActions, addActions, removeActions]);

  const isSelectionBarVisible = canBulkSelect && totalSelectedCount > 0;
  const bulkOverlay =
    isBulkUpdateBannerVisible || isSelectionBarVisible ? (
      <BulkOverlay>
        {isBulkUpdateBannerVisible ? (
          <BulkUpdateJobBanner
            progress={bulkUpdateProgress}
            isLoading={isBulkUpdateProgressLoading}
            isUndoInProgress={isUndoBannerInProgress}
            onUndo={() => void handleUndoBulkUpdate()}
            onClose={closeBulkUpdateBanner}
            operationKind={activeBulkUpdateOperationKind}
            partnerPrefix={partnerPrefix}
          />
        ) : null}
        {isSelectionBarVisible ? (
          <BulkActionBar
            selectedCount={totalSelectedCount}
            totalCount={displayTotalCount}
            onActionsClick={openBulkActionsModal}
            onClearClick={clearSelection}
            isActionsLoading={isCopyConversationsAsMarkdownLoading}
          />
        ) : null}
      </BulkOverlay>
    ) : null;

  return (
    <Box sx={CONVERSATIONS_VIEW_WRAPPER_STYLES}>
      <RecordsGridLayout<T>
        entityName={entity}
        onRecordClose={onRecordClose}
        onRecordClick={onRecordClick}
        onRecordOpenFullPage={onRecordOpenFullPage}
        handleCopyClick={customView ? handleCopyClick : undefined}
        handleUpdateOriginView={isAllowedToUpdate ? handleUpdateOriginView : undefined}
        selectedRecord={selectedRecord}
        slots={{
          ...(slots ?? {}),
          header: customTitle,
          listOverlay: bulkOverlay,
        }}
        slotProps={{
          createButton: {
            onCreateClick,
            entityName: entity,
            ...(slotProps?.createButton ?? {}),
          },
        }}
        leftHeaderAdornment={
          leftHeaderAdornment ?? <GridBreadcrumbItem handleClick={goToViewList} />
        }
        isRecordsLoading={recordsLoading}
        isRecordsLoadingMore={recordsLoadingMore}
        attributesList={attributesList}
        isQueryBuilderOpen={isQueryBuilderOpen}
        sort={sort}
        filters={filters!}
        columns={columns!}
        formMethods={formMethods}
        handleColumnUpdate={handleColumnUpdate}
        handleFilterChange={handleFilterChange}
        handleSortChange={handleSortChange}
        handleColumnsUpdate={handleColumnsUpdate}
        toggleQueryBuilder={toggleQueryBuilder}
        handleScroll={handleScroll}
        handleSearch={isSearchable ? setSearchTerm : undefined}
        records={records as unknown as T[]}
        resetToDefault={resetToDefault}
        totalRecords={totalRecords}
        selectionState={canBulkSelect ? selectionState : undefined}
        sx={sx}
        leftPanelWidth={leftPanelWidth}
        leftPanelMinWidth={leftPanelMinWidth}
        hasDetachedPanel={hasDetachedPanel}
      />

      {canBulkSelect ? (
        <BulkActionsModal
          open={isBulkActionsModalOpen}
          onClose={closeBulkActionsModal}
          onBulkUpdateSubmitted={handleBulkUpdateSubmitted}
          onBulkActionCompleted={handleBulkActionCompleted}
          entity={entity}
          records={selectedVisibleRecords}
          totalCount={displayTotalCount}
          selectedCount={totalSelectedCount}
          partnerId={selectedPartnerId}
          selectionInput={bulkUpdateSelectionInput}
          getEntityUrl={getEntityUrl}
          asyncActions={bulkMarkdownModalAction ? [bulkMarkdownModalAction] : []}
          initialUpdateAttribute={initialBulkUpdateAttribute}
          canBulkEdit={canBulkEdit}
          canBulkDelete={canBulkDelete}
          contextValues={bulkActionContextValues}
        />
      ) : null}
    </Box>
  );
};
